export const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

export const simplifyAddress = (complexAddress) => {
  let simplified = complexAddress
    .replace(/[A-Za-z]+ \d+\/\d+, \d+(st|nd|rd|th) Floor,?/i, "")
    .replace(/[A-Za-z]+ \d+,?/i, "")
    .replace(/Pvt\.? Ltd\.?/i, "")
    .replace(/\s+/g, " ")
    .trim();

  const lower = simplified.toLowerCase();
  if (!lower.includes("vadodara") && !lower.includes("ahmedabad") && !lower.includes("gandhinagar")) {
    simplified += ", Ahmedabad, Gujarat";
  }
  return simplified;
};

export const getCoordinatesFromGoogle = async (address, logCallback, apiKey) => {
  const API_KEY = apiKey || process.env.REACT_APP_GOOGLE_MAPS_KEY;
  const simplifiedAddress = simplifyAddress(address);
  logCallback(`[Geocoding] Trying Google Maps API for: ${simplifiedAddress}`);

  const encodedAddress = encodeURIComponent(simplifiedAddress);
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${API_KEY}`;

  try {
    const response = await fetch(url);
    const data = await response.json();
    logCallback(`[Geocoding] Google Maps API Response:`, data);

    if (data.status === "OK" && data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry.location;
      logCallback(`[Geocoding] Success! Using Google Maps API.`);
      return {
        latitude: parseFloat(lat),
        longitude: parseFloat(lng),
        formattedAddress: data.results[0].formatted_address || simplifiedAddress,
        source: "Google Maps"
      };
    }

    logCallback(`[Geocoding] Google Maps API returned no results for "${simplifiedAddress}".`);
    return null;
  } catch (error) {
    console.error("[Geocoding] Error:", error);
    logCallback(`[Geocoding] Error: ${error.message}`);
    return null;
  }
};

export const fetchRouteMetrics = async (points) => {
  const url = `${process.env.REACT_APP_GRAPHHOPPER_API_URL}/api/GraphHopper/route`;
  const payload = { points, profile: "car", pointsEncoded: false, instructions: false, calcPoints: true };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const data = await response.json();
    if (data.success && data.data.paths.length > 0) {
      const path = data.data.paths[0];
      return { distance: (path.distance / 1000).toFixed(2), time: (path.time / 3600000).toFixed(2) };
    }
    return { distance: 0, time: 0 };
  } catch (error) {
    console.error("Error fetching route metrics:", error);
    return { distance: 0, time: 0 };
  }
};

const OSRM_MANEUVER_TEXT = {
  depart: 'Head', arrive: 'Arrive at destination', turn: 'Turn',
  'new name': 'Continue onto', continue: 'Continue on', fork: 'Keep',
  merge: 'Merge', 'on ramp': 'Take ramp', 'off ramp': 'Take exit',
  roundabout: 'Enter roundabout', rotary: 'Enter rotary',
};

const OSRM_SIGN = {
  'turn-left': -2, 'turn-right': 2, 'turn-sharp-left': -3, 'turn-sharp-right': 3,
  'turn-slight-left': -1, 'turn-slight-right': 1, straight: 0,
  depart: 0, arrive: 5, roundabout: 4,
};

const fetchDirectionsGraphHopper = async (points) => {
  const url = `${process.env.REACT_APP_GRAPHHOPPER_API_URL}/api/GraphHopper/route`;
  const payload = { points, profile: "car", pointsEncoded: false, instructions: true, calcPoints: true };

  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!data.success) throw new Error("GraphHopper unavailable");

  if (data.data?.paths?.length > 0) {
    return data.data.paths[0].instructions.map((i) => ({
      text: i.text,
      distance: i.distance ? `${(i.distance / 1000).toFixed(2)} km` : null,
      sign: i.sign,
      time: i.time ? `${Math.round(i.time / 1000)} sec` : null,
    }));
  }
  return [];
};

const fetchDirectionsOSRM = async (points) => {
  const coords = points.map(p => `${p.longitude},${p.latitude}`).join(';');
  const url = `https://router.project-osrm.org/route/v1/driving/${coords}?steps=true&overview=false`;

  const response = await fetch(url);
  if (!response.ok) throw new Error("OSRM unavailable");

  const data = await response.json();
  if (data.code !== 'Ok' || !data.routes?.length) return [];

  const instructions = [];
  for (const leg of data.routes[0].legs) {
    for (const step of leg.steps) {
      const maneuverType = step.maneuver?.type || '';
      const modifier = step.maneuver?.modifier || '';
      const base = OSRM_MANEUVER_TEXT[maneuverType] || 'Continue';
      const road = step.name ? ` onto ${step.name}` : '';
      const mod = modifier && !['straight', 'uturn'].includes(modifier) ? ` ${modifier}` : '';
      instructions.push({
        text: `${base}${mod}${road}`,
        distance: step.distance ? `${(step.distance / 1000).toFixed(2)} km` : null,
        sign: OSRM_SIGN[modifier ? `${maneuverType}-${modifier}` : maneuverType] ?? 0,
        time: step.duration ? `${Math.round(step.duration)} sec` : null,
      });
    }
  }
  return instructions;
};

export const fetchDirections = async (points) => {
  try {
    return await fetchDirectionsGraphHopper(points);
  } catch {
    try {
      return await fetchDirectionsOSRM(points);
    } catch {
      throw new Error("All routing services are currently unavailable. Please try again later.");
    }
  }
};

export const getDirectionIcon = (sign) => {
  const icons = { 0: '↑', 1: '↗', 2: '→', 3: '↘', '-1': '↖', '-2': '←', '-3': '↙', 4: '🔄', 5: '✓', 6: '🏁', '-7': '↖' };
  return icons[sign] || '↑';
};
